'use client';

import React, { memo, type PropsWithChildren, useCallback, useContext, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import styled, { keyframes } from 'styled-components';

import colors, { withOpacity } from '@wecasa/common/src/utils/colors';
import { NOOP } from '@wecasa/common/src/utils/noop';

import { MODALS_DEFAULT_Z_INDEX } from '../../constants/styles';

export const MODAL_ANIMATION_DURATION = 300;

export const show = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

export const hide = keyframes`
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
`;

const ModalContainer = styled.div<{
  $zIndex: number;
}>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: fixed;
  inset: 0;
  z-index: ${({ $zIndex }) => $zIndex};
`;

const Backdrop = styled.div<{
  $isOpen: boolean;
  $opacity: number;
  $hasBlur: boolean;
}>`
  position: fixed;
  inset: 0;
  z-index: -1;
  background-color: ${({ $opacity }) => withOpacity(colors.ink100, $opacity)};
  ${({ $hasBlur }) => $hasBlur && 'backdrop-filter: blur(6px)'};
  animation: ${({ $isOpen }) => ($isOpen ? show : hide)} ${MODAL_ANIMATION_DURATION}ms ease forwards;
`;

const ModalProviderWrapper = styled.div`
  height: 100%;
`;

const Context = React.createContext(undefined);

type ModalProviderProps = PropsWithChildren<{}>;

export const ModalProvider = ({ children }: ModalProviderProps) => {
  // TODO: type this better
  const modalRef = useRef<any>(null);
  const [context, setContext] = useState();

  // make sure re-render is triggered after initial render so that modalRef exists
  useEffect(() => {
    setContext(modalRef.current);
  }, []);

  return (
    <ModalProviderWrapper>
      <Context.Provider value={context}>{children}</Context.Provider>
      <div ref={modalRef} />
    </ModalProviderWrapper>
  );
};

export type ModalContainerWithBackdropProps = {
  className?: string;
  isOpen: boolean;
  onClose?: () => void;
  backdropOpacity?: number;
  zIndex?: number;
  hasBackdropBlur?: boolean;
};

const ModalContainerWithBackdrop = ({
  children,
  isOpen,
  onClose,
  className,
  backdropOpacity = 0.3,
  zIndex = MODALS_DEFAULT_Z_INDEX,
  hasBackdropBlur = false,
}: PropsWithChildren<ModalContainerWithBackdropProps>): React.JSX.Element | null => {
  const modalNode = useContext(Context);
  const [modalShown, setModalShown] = useState(false);

  const onEscKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      if (onClose) onClose();
    },
    [onClose],
  );

  useEffect(() => {
    let closeTimer: ReturnType<typeof setTimeout> | null = null;

    if (isOpen) {
      setModalShown(true);
    } else {
      closeTimer = setTimeout(() => setModalShown(false), MODAL_ANIMATION_DURATION);
    }

    return () => {
      if (closeTimer) clearTimeout(closeTimer);
    };
  }, [isOpen]);

  useEffect(() => {
    window.addEventListener('keydown', onEscKeyDown, false);
    return () => window.removeEventListener('keydown', onEscKeyDown, false);
  }, [onEscKeyDown]);

  if (!modalNode || !modalShown) return null;
  return ReactDOM.createPortal(
    <ModalContainer $zIndex={zIndex} className={className}>
      <Backdrop
        data-testid='modalBackdrop'
        onClick={onClose || NOOP}
        $isOpen={isOpen}
        $opacity={backdropOpacity}
        $hasBlur={hasBackdropBlur}
      />
      {children}
    </ModalContainer>,
    modalNode,
  );
};

export default memo(ModalContainerWithBackdrop);
